import { useContext, useEffect, useRef } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";

/** BrowserRouter-compatible guard. Its navigator is restored when /sell unmounts.
 * Native confirmation also covers navigation initiated by the existing app header.
 * History indexes let cancelled back/forward navigation return to the same entry. */
export function useLeaveGuard(enabled: boolean) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  useEffect(() => {
    const warning =
      "Leave your toy listing? Save a draft to keep your text. Photos must be reselected after leaving.";
    const allow = () => !enabledRef.current || window.confirm(warning);
    const push = navigator.push;
    const replace = navigator.replace;
    const go = navigator.go;
    navigator.push = (...args) => {
      if (allow()) push(...args);
    };
    navigator.replace = (...args) => {
      if (allow()) replace(...args);
    };
    // Browser back/forward is checked by popstate; MemoryRouter has no popstate.
    navigator.go = (...args) => {
      if (index !== undefined || allow()) go(...args);
    };
    let index: number | undefined = window.history.state?.idx;
    let restoring = false;
    const pop = (event: PopStateEvent) => {
      const nextIndex: number | undefined = event.state?.idx;
      if (restoring) {
        restoring = false;
        event.stopImmediatePropagation();
        return;
      }
      if (
        enabledRef.current &&
        index !== undefined &&
        nextIndex !== undefined &&
        !allow()
      ) {
        restoring = true;
        event.stopImmediatePropagation();
        window.history.go(index - nextIndex);
      } else index = nextIndex;
    };
    const unload = (event: BeforeUnloadEvent) => {
      if (enabledRef.current) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("popstate", pop, true);
    window.addEventListener("beforeunload", unload);
    return () => {
      navigator.push = push;
      navigator.replace = replace;
      navigator.go = go;
      window.removeEventListener("popstate", pop, true);
      window.removeEventListener("beforeunload", unload);
    };
  }, [navigator]);
}
